import React, { useEffect, useState } from "react";
import { Users, UserCog, FileText, TrendingUp, Target, Zap, Database, CheckCircle2, AlertTriangle, Clock, Wifi, Activity } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
};

export default function AdminHome() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [agg, setAgg] = useState(null);            // heavy averages + charts, loaded 2nd
  const [live, setLive] = useState({ live_employees: 0 });
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    // Fast path: counts + backup info. Renders the page instantly.
    (async () => {
      try { const r = await api.get("/admin/stats"); setS(r.data); }
      catch (err) { console.error("AdminHome stats failed:", err); }
    })();
    // Slow path: averages / accuracy buckets / daily chart. Fills in when ready.
    (async () => {
      try { const r = await api.get("/admin/stats/aggregates"); setAgg(r.data); }
      catch (err) { console.error("AdminHome aggregates failed:", err); }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [l, a] = await Promise.all([
          api.get("/admin/live-sessions"),
          api.get("/admin/recent-activity?limit=10"),
        ]);
        if (cancelled) return;
        setLive(l.data || { live_employees: 0 });
        setActivity(a.data?.items || []);
      } catch (err) {
        console.error("AdminHome live data failed:", err);
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!s) return <div className="text-zinc-500">{t("loading")}</div>;

  const lb = s.last_backup;
  const ageH = lb ? lb.age_hours : null;
  const backupHealthy = ageH !== null && ageH <= 26;

  const cards = [
    { label: t("admin_users_title"), value: s.users, icon: Users },
    { label: t("employees_title"), value: s.employees, icon: UserCog },
    { label: t("typing_pages"), value: s.pages.toLocaleString(), icon: FileText },
    { label: t("submitted"), value: s.submissions, icon: Target },
    { label: t("avg_accuracy"), value: agg ? `${agg.avg_accuracy}%` : "…", icon: TrendingUp },
    { label: `${t("wpm")} (avg)`, value: agg ? agg.avg_wpm : "…", icon: Zap },
  ];

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("admin_overview")}</h1>
          <p className="text-zinc-500 mt-2">{t("control_room")}</p>
        </div>
        <a href="/admin/backup" data-testid="backup-pill"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
            backupHealthy
              ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
              : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
          }`}>
          {backupHealthy ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {lb ? (
            <>Last backup: <span className="font-mono">{ageH < 1 ? `${Math.round(ageH * 60)} min ago` : `${Math.round(ageH)} h ago`}</span></>
          ) : (
            <>No backup yet</>
          )}
        </a>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-clean p-5">
              <Icon size={18} className="text-zinc-500" />
              <div className="font-display text-2xl lg:text-3xl font-bold mt-3">{c.value}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      {/* Live employees + Recent login activity */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="card-clean p-6 flex flex-col" data-testid="live-employees-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Employees Currently Online</div>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
          </div>
          <div className="flex items-end gap-3 mt-4">
            <Wifi size={28} className="text-emerald-600 mb-1" />
            <div className="font-display text-5xl font-bold leading-none" data-testid="live-employees-count">
              {live.live_employees}
            </div>
          </div>
          <div className="text-sm text-zinc-500 mt-2">Live Employees</div>
          <div className="text-[11px] text-zinc-400 mt-auto pt-4">Auto-refreshes every 15s</div>
        </div>

        <div className="card-clean p-6 lg:col-span-2" data-testid="recent-activity-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <Activity size={18} className="text-zinc-700" /> Recent Login Activity
            </h2>
            <span className="chip">Last {activity.length || 0}</span>
          </div>
          <div className="max-h-[280px] overflow-y-auto pr-1 -mr-1">
            {activity.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">No recent activity yet.</div>
            ) : (
              <ul className="divide-y divide-[var(--line)]">
                {activity.map((row) => {
                  const online = row.status === "online";
                  return (
                    <li key={row.id || row.session_id} className="py-3 flex items-start justify-between gap-4" data-testid={`activity-row-${row.session_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{row.name || row.email}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          <span className="uppercase tracking-widest text-[10px] text-zinc-400 mr-1">Login:</span>
                          {fmtDateTime(row.login_at)}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          <span className="uppercase tracking-widest text-[10px] text-zinc-400 mr-1">Logout:</span>
                          {online ? <span className="text-emerald-700 font-semibold">Online</span> : fmtDateTime(row.logout_at)}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${
                        online
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-zinc-100 text-zinc-600 border-zinc-200"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
                        {online ? "ONLINE" : "OFFLINE"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="card-clean p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold">{t("stats_daily")}</h2>
            <span className="chip">7d</span>
          </div>
          {agg ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={agg.daily_submissions}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d4af37" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#d4af37" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#d4af37" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] rounded-xl bg-zinc-50 animate-pulse flex items-center justify-center text-xs text-zinc-400">Loading chart…</div>
          )}
        </div>

        <div className="card-clean p-6">
          <h2 className="font-display text-lg font-bold mb-4">{t("stats_buckets")}</h2>
          {agg ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={agg.accuracy_buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0b1f3a" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] rounded-xl bg-zinc-50 animate-pulse flex items-center justify-center text-xs text-zinc-400">Loading chart…</div>
          )}
        </div>
      </div>
    </div>
  );
}
